"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { useTranslations } from "next-intl";

import { logger } from "@/utils/logger";

export interface ImageProcessingState {
  jobId?: string;
  status: "idle" | "uploading" | "processing" | "completed" | "failed";
  progress: number;
  completedImages: string[];
  error?: string;
  isPolling: boolean;
}

export interface UseImageProcessingOptions {
  onComplete?: (images: string[]) => void;
  onError?: (error: string) => void;
  onProgress?: (progress: number) => void;
  pollInterval?: number;
  maxPollDuration?: number;
}

export const useImageProcessing = (options: UseImageProcessingOptions = {}) => {
  const {
    onComplete,
    onError,
    onProgress,
    pollInterval = 2000,
    maxPollDuration = 300000, // 5 minutes
  } = options;

  const t = useTranslations();
  const [state, setState] = useState<ImageProcessingState>({
    status: "idle",
    progress: 0,
    completedImages: [],
    isPolling: false,
  });

  const pollTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const pollStartTimeRef = useRef<number>(undefined);
  const lastKnownProgressRef = useRef<number>(0);
  const cancelledRef = useRef(false);

  /**
   * Reset cancellation flag when startPolling is invoked
   */
  const resetCancellation = () => {
    cancelledRef.current = false;
  };

  /**
   * Start polling for job status
   */
  const startPolling = useCallback(
    (jobId: string) => {
      if (state.isPolling) {
        return; // Already polling
      }

      // Ensure any previous cancellation flag is cleared
      resetCancellation();

      setState((prev) => ({
        ...prev,
        jobId,
        status: "processing",
        isPolling: true,
      }));

      pollStartTimeRef.current = Date.now();
      lastKnownProgressRef.current = 0;

      const poll = async () => {
        try {
          // Check if we've exceeded max poll duration
          if (
            pollStartTimeRef.current &&
            Date.now() - pollStartTimeRef.current > maxPollDuration
          ) {
            setState((prev) => ({
              ...prev,
              status: "failed",
              error: "Processing timeout",
              isPolling: false,
            }));
            onError?.("Processing timeout");
            return;
          }

          const response = await fetch(`/image-processing/${jobId}`, {
            credentials: "include",
          });

          if (!response.ok) {
            // Handle job not found gracefully
            if (response.status === 404) {
              // Schedule next poll – job might not be created yet
              pollTimeoutRef.current = setTimeout(poll, pollInterval);
              return;
            }

            // Stop polling on unauthorized / forbidden
            if (response.status === 401 || response.status === 403) {
              setState((prev) => ({
                ...prev,
                status: "failed",
                error: "Unauthorized – please sign in again.",
                isPolling: false,
              }));
              onError?.("Unauthorized – please sign in again.");
              return;
            }

            throw new Error(`HTTP ${response.status}`);
          }

          const notification = await response.json();

          // Stop immediately if component has been unmounted
          if (cancelledRef.current) {
            return;
          }

          setState((prev) => {
            const newState = {
              ...prev,
              status:
                notification.status === "processing"
                  ? ("processing" as const)
                  : notification.status === "completed"
                    ? ("completed" as const)
                    : notification.status === "failed"
                      ? ("failed" as const)
                      : prev.status,
              progress: notification.progress || 0,
              completedImages: notification.completedImages || [],
              error: notification.error,
            };

            // Only update if progress actually changed to prevent unnecessary re-renders
            if (newState.progress !== lastKnownProgressRef.current) {
              lastKnownProgressRef.current = newState.progress;
              onProgress?.(newState.progress);
            }

            return newState;
          });

          if (notification.status === "completed") {
            setState((prev) => ({ ...prev, isPolling: false }));
            onComplete?.(notification.completedImages || []);
          } else if (notification.status === "failed") {
            setState((prev) => ({ ...prev, isPolling: false }));
            onError?.(notification.error || "Processing failed");
          } else {
            // Continue polling if component still mounted
            if (!cancelledRef.current) {
              pollTimeoutRef.current = setTimeout(poll, pollInterval);
            }
          }
        } catch (error) {
          logger.error("Polling error:", error);
          // Continue polling on network errors, but with longer interval
          if (!cancelledRef.current) {
            pollTimeoutRef.current = setTimeout(poll, pollInterval * 2);
          }
        }
      };

      // Start polling immediately
      poll();
    },
    [
      state.isPolling,
      maxPollDuration,
      pollInterval,
      onComplete,
      onError,
      onProgress,
    ],
  );

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = undefined;
    }
    cancelledRef.current = true;
    setState((prev) => ({ ...prev, isPolling: false }));
  }, []);

  const cancelUpload = useCallback(() => {
    stopPolling();
    setState((prev) => ({
      ...prev,
      status: "idle",
      progress: 0,
      completedImages: [],
      isPolling: false,
    }));
    lastKnownProgressRef.current = 0;
  }, [stopPolling]);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    stopPolling();
    setState({
      status: "idle",
      progress: 0,
      completedImages: [],
      isPolling: false,
    });
    lastKnownProgressRef.current = 0;
  }, [stopPolling]);

  /**
   * Upload images and start processing
   */
  const uploadAndProcess = useCallback(
    async (
      files: File[],
      additionalData?: Record<string, any>,
    ): Promise<string | null> => {
      if (files.length === 0) {
        return null;
      }

      try {
        setState((prev) => ({ ...prev, status: "uploading", progress: 0 }));

        // Upload files to temp storage first
        const tempUrls: string[] = [];

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const formData = new FormData();
          formData.append("image", file);

          const response = await fetch("/api/upload/temp", {
            method: "POST",
            body: formData,
            credentials: "include",
          });

          if (!response.ok) {
            throw new Error(`Upload failed for ${file.name}`);
          }

          const result = await response.json();
          tempUrls.push(result.url);

          // Update upload progress
          const uploadProgress = Math.round(((i + 1) / files.length) * 50); // Upload is 50% of total
          setState((prev) => ({ ...prev, progress: uploadProgress }));
        }

        // Queue for async processing
        const processResponse = await fetch("/api/upload/queue-processing", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            tempImageUrls: tempUrls,
            ...additionalData,
          }),
        });

        if (!processResponse.ok) {
          throw new Error("Failed to queue processing");
        }

        const { jobId } = await processResponse.json();

        // Start polling for processing status
        startPolling(jobId);

        return jobId;
      } catch (error) {
        logger.error("Upload and process error:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        setState((prev) => ({
          ...prev,
          status: "failed",
          error: errorMessage,
        }));
        onError?.(errorMessage);
        return null;
      }
    },
    [startPolling, onError],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
      cancelledRef.current = true;
    };
  }, []);

  return {
    state,
    uploadAndProcess,
    startPolling,
    stopPolling,
    reset,

    // Computed values for easier use
    isIdle: state.status === "idle",
    isUploading: state.status === "uploading",
    isProcessing: state.status === "processing",
    isCompleted: state.status === "completed",
    isFailed: state.status === "failed",
    hasError: !!state.error,

    // Formatted progress text
    progressText:
      state.status === "uploading"
        ? `${t("uploading")}: ${state.progress}%`
        : state.status === "processing"
          ? `${t("processing")}: ${state.progress}%`
          : state.status === "completed"
            ? t("completed")
            : state.status === "failed"
              ? t("failed")
              : t("ready"),
  };
};
